import { ChevronDown, ExternalLink, Loader2, Mail, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { MailAccessLevel, MailAccount, MailPreset, MailSecurity } from "@/lib/types"

type Translate = ReturnType<typeof useT>

/** Access-level options, translated. Keys mirror `mailAccounts.access.*` in the catalogs. */
function accessLevels(t: Translate): { value: MailAccessLevel; label: string; hint: string }[] {
  return [
    { value: "read", label: t("mailAccounts.access.read.label"), hint: t("mailAccounts.access.read.hint") },
    { value: "send", label: t("mailAccounts.access.send.label"), hint: t("mailAccounts.access.send.hint") },
    { value: "full", label: t("mailAccounts.access.full.label"), hint: t("mailAccounts.access.full.hint") },
  ]
}

/** Address domains that pick a provider preset automatically. */
const DOMAIN_PRESETS: Record<string, string> = {
  "gmail.com": "gmail",
  "googlemail.com": "gmail",
  "yahoo.com": "yahoo",
  "icloud.com": "icloud",
  "me.com": "icloud",
  "yandex.ru": "yandex",
  "yandex.com": "yandex",
  "ya.ru": "yandex",
  "mail.ru": "mailru",
  "bk.ru": "mailru",
  "inbox.ru": "mailru",
  "list.ru": "mailru",
  "zoho.com": "zoho",
}

interface FormState {
  preset: string
  email: string
  display_name: string
  label: string
  password: string
  access_level: MailAccessLevel
  imap_host: string
  imap_port: string
  imap_security: MailSecurity
  imap_username: string
  smtp_host: string
  smtp_port: string
  smtp_security: MailSecurity
  smtp_username: string
  sent_folder: string
  append_sent: boolean
}

type UpdateField = <K extends keyof FormState>(key: K, value: FormState[K]) => void

const EMPTY_FORM: FormState = {
  preset: "other",
  email: "",
  display_name: "",
  label: "",
  password: "",
  access_level: "read",
  imap_host: "",
  imap_port: "993",
  imap_security: "ssl",
  imap_username: "",
  smtp_host: "",
  smtp_port: "465",
  smtp_security: "ssl",
  smtp_username: "",
  sent_folder: "",
  append_sent: true,
}

function formFromAccount(account: MailAccount): FormState {
  return {
    preset: "other",
    email: account.email,
    display_name: account.display_name ?? "",
    label: account.label,
    password: "",
    access_level: account.access_level,
    imap_host: account.imap_host,
    imap_port: String(account.imap_port),
    imap_security: account.imap_security,
    imap_username: account.imap_username === account.email ? "" : account.imap_username,
    smtp_host: account.smtp_host,
    smtp_port: String(account.smtp_port),
    smtp_security: account.smtp_security,
    smtp_username: account.smtp_username === account.email ? "" : account.smtp_username,
    sent_folder: account.sent_folder ?? "",
    append_sent: account.append_sent,
  }
}

function applyPreset(form: FormState, preset: MailPreset | undefined, key: string): FormState {
  if (!preset) return { ...form, preset: key }
  return {
    ...form,
    preset: key,
    imap_host: preset.imap_host,
    imap_port: String(preset.imap_port),
    imap_security: preset.imap_security,
    smtp_host: preset.smtp_host,
    smtp_port: String(preset.smtp_port),
    smtp_security: preset.smtp_security,
    append_sent: preset.append_sent,
  }
}

function sortByLabel(accounts: MailAccount[]): MailAccount[] {
  return [...accounts].sort((a, b) => a.label.localeCompare(b.label))
}

/** Unlimited IMAP/SMTP accounts for one agent (rendered inside the Mail plugin's detail). */
export function MailAccountsPanel({ agentId }: { agentId: string }) {
  const t = useT()
  const [loading, setLoading] = React.useState(true)
  const [accounts, setAccounts] = React.useState<MailAccount[]>([])
  const [presets, setPresets] = React.useState<MailPreset[]>([])
  const [editing, setEditing] = React.useState<MailAccount | "new" | null>(null)
  const [busyId, setBusyId] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    try {
      const [rows, presetList] = await Promise.all([
        api.listMailAccounts(agentId),
        api.mailPresets(agentId),
      ])
      setAccounts(rows)
      setPresets(presetList)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("mailAccounts.toasts.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [agentId, t])

  React.useEffect(() => {
    void load()
  }, [load])

  function replace(account: MailAccount) {
    setAccounts((current) =>
      sortByLabel([...current.filter((row) => row.id !== account.id), account]),
    )
  }

  function reportStatus(account: MailAccount) {
    if (account.status === "ok") toast.success(t("mailAccounts.toasts.connected", { email: account.email }))
    else toast.error(account.last_error ?? t("mailAccounts.toasts.connectFailed", { email: account.email }))
  }

  async function retest(account: MailAccount) {
    setBusyId(account.id)
    try {
      const updated = await api.testMailAccount(agentId, account.id)
      replace(updated)
      reportStatus(updated)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("mailAccounts.toasts.testFailed"))
    } finally {
      setBusyId(null)
    }
  }

  async function remove(account: MailAccount) {
    if (!confirm(t("mailAccounts.removeConfirm", { email: account.email }))) {
      return
    }
    setBusyId(account.id)
    try {
      await api.deleteMailAccount(agentId, account.id)
      setAccounts((current) => current.filter((row) => row.id !== account.id))
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("mailAccounts.toasts.removeFailed"))
    } finally {
      setBusyId(null)
    }
  }

  function saved(account: MailAccount) {
    replace(account)
    setEditing(null)
    reportStatus(account)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="size-4" />
              {t("mailAccounts.title")}
            </CardTitle>
            <CardDescription>{t("mailAccounts.description")}</CardDescription>
          </div>
          <Button type="button" size="sm" onClick={() => setEditing("new")}>
            <Plus className="size-4" />
            {t("mailAccounts.addAccount")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-muted-foreground text-sm">{t("mailAccounts.loading")}</p>
        ) : accounts.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("mailAccounts.empty")}</p>
        ) : (
          accounts.map((account) => (
            <AccountRow
              key={account.id}
              account={account}
              busy={busyId === account.id}
              onTest={() => void retest(account)}
              onEdit={() => setEditing(account)}
              onDelete={() => void remove(account)}
            />
          ))
        )}
      </CardContent>
      {editing !== null && (
        <AccountDialog
          agentId={agentId}
          presets={presets}
          account={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={saved}
        />
      )}
    </Card>
  )
}

function AccountRow({
  account,
  busy,
  onTest,
  onEdit,
  onDelete,
}: {
  account: MailAccount
  busy: boolean
  onTest: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const t = useT()
  const level = accessLevels(t).find((item) => item.value === account.access_level)
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-medium">{account.label}</span>
          <Badge variant="outline">{level?.label ?? account.access_level}</Badge>
          {account.status === "ok" && (
            <Badge variant="secondary">{t("common.status.connection.connected")}</Badge>
          )}
          {account.status === "error" && (
            <Badge variant="destructive">{t("mailAccounts.status.error")}</Badge>
          )}
          {account.status === "unchecked" && (
            <Badge variant="outline">{t("common.status.connection.notChecked")}</Badge>
          )}
        </div>
        <p className="text-muted-foreground truncate text-xs">{account.email}</p>
        {account.status === "error" && account.last_error && (
          <p className="text-destructive mt-1 text-xs">{account.last_error}</p>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("mailAccounts.testAria", { email: account.email })}
          onClick={onTest}
          disabled={busy}
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("mailAccounts.editAria", { email: account.email })}
          onClick={onEdit}
          disabled={busy}
        >
          <Pencil className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("mailAccounts.removeAria", { email: account.email })}
          onClick={onDelete}
          disabled={busy}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    </div>
  )
}

function AccountDialog({
  agentId,
  presets,
  account,
  onClose,
  onSaved,
}: {
  agentId: string
  presets: MailPreset[]
  account: MailAccount | null
  onClose: () => void
  onSaved: (account: MailAccount) => void
}) {
  const t = useT()
  const [form, setForm] = React.useState<FormState>(() =>
    account ? formFromAccount(account) : EMPTY_FORM,
  )
  const [advanced, setAdvanced] = React.useState(account !== null)
  const [saving, setSaving] = React.useState(false)
  const preset = presets.find((item) => item.key === form.preset)
  const level = accessLevels(t).find((item) => item.value === form.access_level)

  const update: UpdateField = (key, value) => setForm((current) => ({ ...current, [key]: value }))

  function choosePreset(key: string) {
    setForm((current) => applyPreset(current, presets.find((item) => item.key === key), key))
    if (key === "other") setAdvanced(true)
  }

  function changeEmail(value: string) {
    setForm((current) => {
      const next = { ...current, email: value }
      const guess = DOMAIN_PRESETS[value.split("@")[1]?.toLowerCase() ?? ""]
      if (!account && guess && current.preset === "other" && !current.imap_host) {
        return applyPreset(next, presets.find((item) => item.key === guess), guess)
      }
      return next
    })
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!account && !form.password.trim()) {
      toast.error(t("mailAccounts.dialog.needPassword"))
      return
    }
    if (!form.imap_host.trim() || !form.smtp_host.trim()) {
      setAdvanced(true)
      toast.error(t("mailAccounts.dialog.needServers"))
      return
    }
    const payload: Record<string, unknown> = {
      email: form.email.trim(),
      display_name: form.display_name.trim() || null,
      label: form.label.trim() || null,
      access_level: form.access_level,
      imap_host: form.imap_host.trim(),
      imap_port: Number(form.imap_port),
      imap_security: form.imap_security,
      imap_username: form.imap_username.trim() || null,
      smtp_host: form.smtp_host.trim(),
      smtp_port: Number(form.smtp_port),
      smtp_security: form.smtp_security,
      smtp_username: form.smtp_username.trim() || null,
      sent_folder: form.sent_folder.trim() || null,
      append_sent: form.append_sent,
    }
    if (form.password.trim()) payload.password = form.password
    setSaving(true)
    try {
      const result = account
        ? await api.updateMailAccount(agentId, account.id, payload)
        : await api.createMailAccount(agentId, payload)
      onSaved(result)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("mailAccounts.toasts.saveFailed"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {account
              ? t("mailAccounts.dialog.editTitle", { email: account.email })
              : t("mailAccounts.dialog.addTitle")}
          </DialogTitle>
          <DialogDescription>{t("mailAccounts.dialog.description")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="mail-provider">{t("mailAccounts.dialog.providerLabel")}</Label>
            <Select value={form.preset} onValueChange={choosePreset}>
              <SelectTrigger id="mail-provider" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {presets.map((item) => (
                  <SelectItem key={item.key} value={item.key}>
                    {item.label}
                  </SelectItem>
                ))}
                <SelectItem value="other">{t("mailAccounts.dialog.providerOther")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="mail-email">{t("mailAccounts.dialog.emailLabel")}</Label>
            <Input
              id="mail-email"
              type="email"
              required
              autoComplete="off"
              value={form.email}
              onChange={(event) => changeEmail(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="mail-password">{t("mailAccounts.dialog.passwordLabel")}</Label>
            <Input
              id="mail-password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(event) => update("password", event.target.value)}
              placeholder={
                account
                  ? account.password_last4
                    ? t("mailAccounts.dialog.passwordPlaceholderLast4", { last4: account.password_last4 })
                    : t("mailAccounts.dialog.passwordPlaceholderPlain")
                  : undefined
              }
            />
            {preset && (
              <a
                href={preset.help_url}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground inline-flex items-center gap-1 text-xs underline"
              >
                {t("mailAccounts.dialog.helpLink", { provider: preset.label })}
                <ExternalLink className="size-3" />
              </a>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="mail-display-name">{t("mailAccounts.dialog.displayNameLabel")}</Label>
              <Input
                id="mail-display-name"
                value={form.display_name}
                onChange={(event) => update("display_name", event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="mail-label">{t("mailAccounts.dialog.labelLabel")}</Label>
              <Input
                id="mail-label"
                value={form.label}
                placeholder={t("mailAccounts.dialog.labelPlaceholder")}
                onChange={(event) => update("label", event.target.value.toLowerCase())}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="mail-access">{t("mailAccounts.dialog.accessLabel")}</Label>
            <Select
              value={form.access_level}
              onValueChange={(value) => update("access_level", value as MailAccessLevel)}
            >
              <SelectTrigger id="mail-access" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {accessLevels(t).map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">{level?.hint}</p>
          </div>
          <Collapsible open={advanced} onOpenChange={setAdvanced}>
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" size="sm">
                <ChevronDown className={`size-4 transition-transform ${advanced ? "rotate-180" : ""}`} />
                {t("mailAccounts.dialog.advanced")}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-2">
              <ServerFields kind="imap" title={t("mailAccounts.dialog.imapTitle")} form={form} update={update} />
              <ServerFields kind="smtp" title={t("mailAccounts.dialog.smtpTitle")} form={form} update={update} />
              <div className="space-y-1">
                <Label htmlFor="mail-sent-folder">{t("mailAccounts.dialog.sentFolderLabel")}</Label>
                <Input
                  id="mail-sent-folder"
                  value={form.sent_folder}
                  placeholder={t("mailAccounts.dialog.sentFolderPlaceholder")}
                  onChange={(event) => update("sent_folder", event.target.value)}
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="mail-append-sent" className="font-normal">
                  {t("mailAccounts.dialog.appendSentLabel")}
                </Label>
                <Switch
                  id="mail-append-sent"
                  checked={form.append_sent}
                  onCheckedChange={(checked) => update("append_sent", checked)}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {account ? t("mailAccounts.dialog.save") : t("mailAccounts.addAccount")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ServerFields({
  kind,
  title,
  form,
  update,
}: {
  kind: "imap" | "smtp"
  title: string
  form: FormState
  update: UpdateField
}) {
  const t = useT()
  const host = `${kind}_host` as const
  const port = `${kind}_port` as const
  const security = `${kind}_security` as const
  const username = `${kind}_username` as const
  return (
    <fieldset className="space-y-2 rounded-lg border p-3">
      <legend className="px-1 text-xs font-medium">{title}</legend>
      <div className="grid gap-2 sm:grid-cols-[1fr_6rem_8rem]">
        <Input
          aria-label={t("mailAccounts.dialog.hostAria", { title })}
          placeholder="mail.example.com"
          value={form[host]}
          onChange={(event) => update(host, event.target.value)}
        />
        <Input
          aria-label={t("mailAccounts.dialog.portAria", { title })}
          inputMode="numeric"
          value={form[port]}
          onChange={(event) => update(port, event.target.value.replace(/\D/g, ""))}
        />
        <Select
          value={form[security]}
          onValueChange={(value) => update(security, value as MailSecurity)}
        >
          <SelectTrigger aria-label={t("mailAccounts.dialog.securityAria", { title })} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ssl">{t("mailAccounts.dialog.securitySsl")}</SelectItem>
            <SelectItem value="starttls">{t("mailAccounts.dialog.securityStarttls")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Input
        aria-label={t("mailAccounts.dialog.usernameAria", { title })}
        placeholder={t("mailAccounts.dialog.usernamePlaceholder")}
        value={form[username]}
        onChange={(event) => update(username, event.target.value)}
      />
    </fieldset>
  )
}
